import { Fragment, useEffect, useState, useRef, useCallback } from "react";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";

import { TrashIcon, ChevronDownIcon, LinkIcon } from "components/Icons";
import { useNavigate } from "react-router";
import FormField from "componentsv2/form/FormField";
import proxy from "api/proxy";
import { cn, colors } from "tools/ui";
import { theme } from "theme/theme";

const style = "appearance-none pt-2 pb-2 px-2 w-full bg-white";

/**
 * Field Form Selection.
 * @param {object} props - component props
 * @param {string} props.name - field name
 * @param {string} props.label - field label
 * @param {boolean} props.readOnly - whether field is read-only
 * @param {string} props.model - model name to search for options
 * @param {string} props.placeholder - string for view in placeholder
 * @param {function} props.recSearch - function to generate search domain based on query and current record
 * @param {Array} props.options - list of options if model is not provided
 * @param {Array} props.attrs - list of fields to include in the option objects if model is provided
 * @param {object} props.record - current record object
 * @param {function} props.onChange - callback function called when field value changes
 * @param {any} props.value - field value
 * @param {boolean} props.remoteSearch - load the first `limit` records and search the server while typing
 * @return {Component} - selection component
 */
function Dropdown(props) {
  const {
    attrs,
    field,
    data,
    parentRec,
    name,
    model,
    readOnly,
    recSearch,
    placeholder,
    record,
    label,
    recName,
    onChange,
    preloaded,
    preOptions,
    colSpan,
    limit = 50,
    remoteSearch,
  } = props;
  const navigate = useNavigate();
  const timeoutId = useRef(null);
  const requestId = useRef(0);
  const [options, setOptions] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const fieldName = recName || "rec_name";

  let fieldsNames = ["id", fieldName];
  if (attrs) {
    fieldsNames = [...fieldsNames, ...attrs];
  }

  function handleFilter(clause) {
    if (clause === "") {
      return;
    }
    const regex = new RegExp(clause.replace(" ", "|"), "i");
    const elements = options.filter((rec) => {
      const targetText = rec.name || rec.rec_name;
      return regex.test(targetText);
    });
    setFiltered(elements);
  }

  const getOptions = useCallback(
    async (_data) => {
      const domain = [];
      if (recSearch) {
        const _parent = parentRec ? parentRec.active : null;
        const domainAdd = await recSearch(_data, record, _parent);
        if (domainAdd === null) return;
        domain.push(domainAdd);
      }
      if (_data && _data !== "") {
        const clause = ["OR"];
        const target = _data.split(" ");
        target.forEach((tw) => {
          if (tw.length <= 1) return;
          clause.push([fieldName, "ilike", `%${tw}%`]);
        });
        if (clause.length > 1) domain.push(clause);
      }
      const targetModel = field?.model || model;
      const currentRequest = ++requestId.current;
      const { data: res } = await proxy.search(
        targetModel,
        domain,
        fieldsNames,
        limit,
      );
      if (currentRequest !== requestId.current) return;
      setOptions(res ?? []);
      setFiltered(res ?? []);
    },
    [recSearch, parentRec, record, field, fieldName, fieldsNames, limit],
  );

  function onOpen() {
    if (!preOptions) {
      getOptions();
    }
  }

  function onRemove() {
    handleChange(null);
    setFiltered(options);
  }

  function handleChange(value) {
    data.value = value;
    if (onChange) {
      onChange(name, value);
    }
  }

  function onChangeInput(event) {
    const data = event.target.value;
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    if (remoteSearch) {
      handleFilter(data);
      timeoutId.current = setTimeout(getOptions, 400, data);
    } else if (preOptions || preloaded) {
      handleFilter(data);
    } else if (data?.length > 2) {
      timeoutId.current = setTimeout(getOptions, 500, data);
    }
  }

  let color = "";
  let styleDisabled = "";
  let chevronIconBg = "bg-white";
  if (readOnly) {
    color = colors.readOnly;
    chevronIconBg = colors.readOnly;
    styleDisabled = "pointer-events-none";
  }

  let nameRec = "";
  if (data?.value && typeof data.value === "object") {
    nameRec = data.value.name ?? data.value.rec_name;
  } else if (record) {
    let _name = name;
    if (record && typeof record[name] === "number") {
      _name = `${name}.`;
    }
    if (record[_name]) {
      nameRec = record[_name].name || record[_name].rec_name;
    }
  }

  useEffect(() => {
    async function fetchOptions() {
      let values = [];
      if (Array.isArray(preOptions)) {
        values = preOptions;
      } else if (typeof preOptions === "function") {
        values = await preOptions(record);
      }

      // Only update if values are different to prevent infinite loops
      if (JSON.stringify(values) !== JSON.stringify(options)) {
        setOptions(values);
        setFiltered(values);
      }
    }
    if (preOptions) {
      fetchOptions();
      return;
    }
    if (preloaded || remoteSearch) {
      getOptions();
      return;
    }
  }, [preOptions]);

  return (
    <FormField
      label={label}
      name={name}
      required={field?.required}
      colspan={colSpan}
      model={model}
    >
      <Combobox
        className="flex overflow-visible"
        as="div"
        value={nameRec}
        onChange={handleChange}
      >
        <div className="relative w-full ">
          <ComboboxInput
            disabled={readOnly}
            readOnly={readOnly}
            className={cn(
              style,
              color,
              theme.textColor,
              theme.border,
              theme.input.height,
            )}
            onChange={onChangeInput}
            displayValue={nameRec}
            placeholder={placeholder}
          />
          <ComboboxButton
            disabled={readOnly}
            readOnly={readOnly}
            onClick={onOpen}
            className={cn(
              "absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none",
              styleDisabled,
            )}
          >
            {!data.value?.id ? (
              <ChevronDownIcon
                className={cn(
                  "fill-current",
                  chevronIconBg,
                  theme.textColor,
                  theme.input.iconSize,
                )}
                aria-hidden="true"
              />
            ) : (
              !readOnly && (
                <TrashIcon
                  onClick={onRemove}
                  className={cn(
                    "bg-white my-auto active:text-rose-600 cursor-pointer active:scale-90 fill-current",
                    theme.textColor,
                    theme.input.iconSize,
                  )}
                  aria-hidden="true"
                />
              )
            )}
          </ComboboxButton>
          {field?.link && data.value && readOnly && (
            <div
              className="absolute inset-y-0 right-0 flex items-center px-2 cursor-pointer z-10"
              onClick={() =>
                navigate(`/dash/${field.link}?id=${data.value.id}`)
              }
            >
              <LinkIcon className="fill-current w-5 h-5 text-neutral-700 hover:text-neutral-600" />
            </div>
          )}
          {filtered?.length > 0 && (
            <ComboboxOptions className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-neutral-200 ring-opacity-5 focus:outline-none sm:text-sm">
              {filtered.map((option) => (
                <ComboboxOption
                  key={option.id}
                  value={option}
                  className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-blue-400 data-[focus]:text-white"
                >
                  {({ selected }) => (
                    <Fragment>
                      <span
                        className={cn(
                          "block truncate",
                          selected && "font-semibold",
                        )}
                      >
                        {option.name || option.rec_name}
                      </span>
                    </Fragment>
                  )}
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          )}
        </div>
      </Combobox>
    </FormField>
  );
}

export default Dropdown;
